import React, { useState, useEffect } from 'react';
import { reconciliationService, ReconciliationSetup } from '../services/reconciliationService';
import toast from 'react-hot-toast';

interface BeginReconciliationProps {
  isOpen: boolean;
  onClose: () => void;
  bankAccountId: string;
  onReconciliationStarted: (data: any) => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
  outline: 'none', colorScheme: 'light',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase',
  color: '#6b6a5c', marginBottom: 6,
};

const BeginReconciliation: React.FC<BeginReconciliationProps> = ({
  isOpen,
  onClose,
  bankAccountId,
  onReconciliationStarted,
}) => {
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState<ReconciliationSetup | null>(null);
  const [formData, setFormData] = useState({
    statementDate: new Date().toISOString().split('T')[0],
    statementEndingBalance: '',
    serviceCharge: '0.00',
    serviceChargeDate: new Date().toISOString().split('T')[0],
    serviceChargeAccount: 'Bank Service Charges',
    interestEarned: '0.00',
    interestEarnedDate: new Date().toISOString().split('T')[0],
    interestEarnedAccount: 'Interest Income',
  });

  useEffect(() => {
    if (isOpen && bankAccountId) {
      loadSetupData();
    }
  }, [isOpen, bankAccountId]);

  const loadSetupData = async () => {
    try {
      const data = await reconciliationService.getReconciliationSetup(bankAccountId);
      setSetupData(data);
    } catch (error) {
      console.error('Error loading reconciliation setup:', error);
      toast.error('Failed to load reconciliation setup');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await reconciliationService.startReconciliation(bankAccountId, {
        statementDate: formData.statementDate,
        statementEndingBalance: parseFloat(formData.statementEndingBalance),
        serviceCharge: parseFloat(formData.serviceCharge) || 0,
        serviceChargeDate: formData.serviceChargeDate || undefined,
        serviceChargeAccount: formData.serviceChargeAccount || undefined,
        interestEarned: parseFloat(formData.interestEarned) || 0,
        interestEarnedDate: formData.interestEarnedDate || undefined,
        interestEarnedAccount: formData.interestEarnedAccount || undefined,
      });

      toast.success('Reconciliation started successfully');
      onReconciliationStarted(data);
      onClose();
    } catch (error: any) {
      console.error('Error starting reconciliation:', error);
      toast.error(error.response?.data?.message || 'Failed to start reconciliation');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(7,7,15,0.85)',
      overflowY: 'auto', zIndex: 50, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      paddingTop: 80, paddingBottom: 40,
    }}>
      <div style={{
        background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)',
        borderRadius: 8, width: '100%', maxWidth: 600, padding: '28px 32px',
        position: 'relative',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid rgba(92,78,51,0.15)',
        }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300, color: '#1c2033' }}>
            Begin Reconciliation
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
              color: '#6b6a5c', cursor: 'pointer', padding: '4px 8px',
              fontFamily: "'Inter', sans-serif", fontSize: 16, lineHeight: 1,
              transition: 'color 0.15s, border-color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#d97706'; e.currentTarget.style.borderColor = 'rgba(217,119,6,0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#8a8778'; e.currentTarget.style.borderColor = 'rgba(92,78,51,0.25)'; }}
          >
            ✕
          </button>
        </div>

        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', marginBottom: 24, margin: '0 0 24px 0' }}>
          Select an account to reconcile, and then enter the ending balance from your account statement.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Account Info */}
            <div>
              <label style={labelStyle}>Account</label>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 14px', background: '#faf7ee',
                border: '1px solid rgba(92,78,51,0.15)', borderRadius: 4,
              }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#1c2033', fontWeight: 500 }}>
                  {setupData?.account.accountName} {setupData?.account.accountNumber}
                </span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
                  last reconciled on{' '}
                  {setupData?.lastReconciliation
                    ? new Date(setupData.lastReconciliation.date).toLocaleDateString()
                    : 'Never'}
                </span>
              </div>
            </div>

            {/* Statement Date */}
            <div>
              <label style={labelStyle}>Statement Date</label>
              <input
                type="date"
                value={formData.statementDate}
                onChange={(e) => setFormData({ ...formData, statementDate: e.target.value })}
                style={inputStyle}
                required
              />
            </div>

            {/* Beginning Balance */}
            <div>
              <label style={labelStyle}>Beginning Balance</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <input
                  type="text"
                  value={
                    setupData?.lastReconciliation?.endingBalance.toFixed(2) ||
                    setupData?.account.balance.toFixed(2) ||
                    '0.00'
                  }
                  readOnly
                  style={{
                    ...inputStyle, width: 120, textAlign: 'right',
                    background: '#faf7ee', color: '#6b6a5c', cursor: 'default',
                  }}
                />
                <span
                  style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5a8cc9', cursor: 'pointer' }}
                  onClick={(e) => e.preventDefault()}
                >
                  What if my beginning balance doesn't match my statement?
                </span>
              </div>
            </div>

            {/* Ending Balance */}
            <div>
              <label style={labelStyle}>Ending Balance</label>
              <input
                type="number"
                step="0.01"
                value={formData.statementEndingBalance}
                onChange={(e) => setFormData({ ...formData, statementEndingBalance: e.target.value })}
                style={{ ...inputStyle, width: 120, textAlign: 'right' }}
                required
              />
            </div>

            {/* Service Charge + Interest Earned */}
            <div style={{ borderTop: '1px solid rgba(92,78,51,0.15)', paddingTop: 20 }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 14, margin: '0 0 14px 0' }}>
                Service Charge &amp; Interest
              </p>

              {/* Service Charge Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Service Charge</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.serviceCharge}
                    onChange={(e) => setFormData({ ...formData, serviceCharge: e.target.value })}
                    style={{ ...inputStyle, textAlign: 'right' }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Date</label>
                  <input
                    type="date"
                    value={formData.serviceChargeDate}
                    onChange={(e) => setFormData({ ...formData, serviceChargeDate: e.target.value })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Account</label>
                  <select
                    value={formData.serviceChargeAccount}
                    onChange={(e) => setFormData({ ...formData, serviceChargeAccount: e.target.value })}
                    style={inputStyle}
                  >
                    <option value="Bank Service Charges">Bank Service Charges</option>
                    <option value="Other Charges">Other Charges</option>
                  </select>
                </div>
              </div>

              {/* Interest Earned Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Interest Earned</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.interestEarned}
                    onChange={(e) => setFormData({ ...formData, interestEarned: e.target.value })}
                    style={{ ...inputStyle, textAlign: 'right' }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Date</label>
                  <input
                    type="date"
                    value={formData.interestEarnedDate}
                    onChange={(e) => setFormData({ ...formData, interestEarnedDate: e.target.value })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Account</label>
                  <select
                    value={formData.interestEarnedAccount}
                    onChange={(e) => setFormData({ ...formData, interestEarnedAccount: e.target.value })}
                    style={inputStyle}
                  >
                    <option value="Interest Income">Interest Income</option>
                    <option value="Other Income">Other Income</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            marginTop: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            paddingTop: 20, borderTop: '1px solid rgba(92,78,51,0.15)',
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'none', border: 'none', fontFamily: "'Inter', sans-serif",
                fontSize: 12, color: '#6b6a5c', cursor: 'pointer', padding: '8px 0',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#5b5a52'}
              onMouseLeave={e => e.currentTarget.style.color = '#8a8778'}
            >
              Cancel
            </button>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                style={{
                  padding: '9px 18px', background: 'none',
                  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
                  fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 400,
                  letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c',
                  cursor: 'pointer', transition: 'color 0.15s, border-color 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#5b5a52'; e.currentTarget.style.borderColor = 'rgba(217,119,6,0.25)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#8a8778'; e.currentTarget.style.borderColor = 'rgba(92,78,51,0.25)'; }}
              >
                Locate Discrepancies
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '9px 24px',
                  background: loading ? 'rgba(92,78,51,0.25)' : 'linear-gradient(135deg, #d97706, #b45309)',
                  border: 'none', borderRadius: 4,
                  fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500,
                  letterSpacing: '0.15em', textTransform: 'uppercase',
                  color: loading ? '#c9c2ab' : '#1c2033',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'opacity 0.15s',
                }}
              >
                {loading ? 'Starting...' : 'Continue'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BeginReconciliation;
